import { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { listOrders, type UserOrganization } from '@vn-propane/shared/client';
import type { OrderSummary, ErrorResponse, OrderStatus } from '@vn-propane/shared/client';
import { Plus, Package, Truck, CheckCircle, XCircle, Clock, Store, User, CreditCard, Phone, MapPin, Banknote, CheckCircle2, AlertTriangle } from 'lucide-react';
import { formatPrice } from '@/lib/utils';

type OutletContext = {
  organization: UserOrganization;
};

export default function OrdersLayout() {
  const { id: orgId } = useParams<{ id: string }>();
  const { t } = useTranslation(['admin', 'common']);
  const { organization } = useOutletContext<OutletContext>();
  const [orders, setOrders] = useState<OrderSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);

  const fetchOrders = useCallback(async () => {
    if (!orgId) return;

    setLoading(true);
    try {
      const result = await listOrders({
        path: { org_id: orgId },
      });

      if (result.error) {
        const errorResponse = result.error as ErrorResponse;
        setError(errorResponse.error?.message || t('orders.failedToLoad'));
        return;
      }

      if (result.data) {
        setOrders(result.data.data);
      }
      setError(null);
    } catch (err) {
      setError(t('orders.failedToLoad'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const awaitingCashOrders = orders.filter(
    o => o.payment_method === 'cash' && !o.is_paid && o.status !== 'completed' && o.status !== 'cancelled'
  );

  const filteredOrders = statusFilter
    ? orders.filter(o => o.status === statusFilter)
    : orders;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const statusConfig: Record<OrderStatus, { label: string; variant: 'default' | 'secondary' | 'outline' | 'destructive'; icon: typeof Clock }> = {
    pending: { label: t('orders.statusPending'), variant: 'secondary', icon: Clock },
    assigned: { label: t('orders.statusAssigned'), variant: 'default', icon: Truck },
    in_progress: { label: t('orders.statusInProgress'), variant: 'default', icon: Truck },
    arrived: { label: t('orders.statusArrived'), variant: 'default', icon: Truck },
    delivered: { label: t('orders.statusDelivered'), variant: 'outline', icon: CheckCircle },
    completed: { label: t('orders.statusCompleted'), variant: 'outline', icon: CheckCircle2 },
    cancelled: { label: t('orders.statusCancelled'), variant: 'destructive', icon: XCircle },
  };

  if (loading) {
    return <div className="flex justify-center p-8">{t('common:labels.loading')}</div>;
  }

  if (error) {
    return <div className="text-red-500 p-4">{error}</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-extrabold tracking-tight">{t('orders.title')}</h2>
          <p className="text-muted-foreground">
            {t('orders.orderCount', { count: orders.length })}
          </p>
        </div>
        <Button asChild>
          <Link to={`/organizations/${orgId}/orders/new`}>
            <Plus className="w-4 h-4 mr-2" />
            {t('orders.newOrder')}
          </Link>
        </Button>
      </div>

      {/* Status Filter */}
      <div className="flex gap-2 flex-wrap">
        <Button
          variant={statusFilter === null ? 'default' : 'outline'}
          size="sm"
          onClick={() => setStatusFilter(null)}
        >
          {t('orders.all')} ({orders.length})
        </Button>
        {Object.entries(statusConfig).map(([status, config]) => {
          const count = orders.filter(o => o.status === status).length;
          return (
            <Button
              key={status}
              variant={statusFilter === status ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatusFilter(status)}
            >
              {config.label} ({count})
            </Button>
          );
        })}
      </div>

      {/* Awaiting Cash Collection - Priority Section */}
      {awaitingCashOrders.length > 0 && !statusFilter && (
        <div className="rounded-lg border-2 border-orange-400 bg-orange-50 dark:bg-orange-950/30 overflow-hidden">
          <div className="flex items-center gap-3 px-4 py-3 bg-orange-100 dark:bg-orange-900/40 border-b border-orange-300">
            <AlertTriangle className="w-5 h-5 text-orange-600 shrink-0" />
            <div className="flex-1">
              <h3 className="font-semibold text-orange-800 dark:text-orange-200">
                {t('orders.awaitingCashTitle')}
              </h3>
              <p className="text-sm text-orange-700 dark:text-orange-300">
                {t('orders.awaitingCashDescription')}
              </p>
            </div>
            <Badge variant="destructive" className="bg-orange-600 text-white shrink-0">
              {awaitingCashOrders.length}
            </Badge>
          </div>
          <div className="divide-y divide-orange-200 dark:divide-orange-800">
            {awaitingCashOrders.map((order) => (
              <Link
                key={order.id}
                to={`/organizations/${orgId}/orders/${order.id}`}
                className="block p-4 hover:bg-orange-100/50 dark:hover:bg-orange-900/20 transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-sm font-mono">
                        #{order.id.slice(-8).toUpperCase()}
                      </span>
                      <Badge variant="outline" className="flex items-center gap-1 border-orange-400 text-orange-700 dark:text-orange-300">
                        <Banknote className="w-3 h-3" />
                        {t('orders.awaitingCashCollection')}
                      </Badge>
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                      {order.customer_name && (
                        <div className="flex items-center gap-1">
                          <User className="w-3.5 h-3.5" />
                          <span>{order.customer_name}</span>
                        </div>
                      )}
                      {order.customer_phone && (
                        <div className="flex items-center gap-1">
                          <Phone className="w-3.5 h-3.5" />
                          <span>{order.customer_phone}</span>
                        </div>
                      )}
                      {order.gas_station_name && (
                        <div className="flex items-center gap-1">
                          <Store className="w-3.5 h-3.5" />
                          <span>{order.gas_station_name}</span>
                        </div>
                      )}
                      {order.driver_name && (
                        <div className="flex items-center gap-1">
                          <Truck className="w-3.5 h-3.5" />
                          <span>{order.driver_name}</span>
                        </div>
                      )}
                    </div>
                    {order.customer_address && (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                        <MapPin className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">{order.customer_address}</span>
                      </div>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-bold text-orange-700 dark:text-orange-300 text-lg">
                      {formatPrice(order.total_amount.toString(), organization.currency)}
                    </div>
                    <div className="flex items-center gap-1 text-xs text-orange-600 justify-end mt-0.5">
                      <Banknote className="w-3 h-3" />
                      <span>{t('orders.confirmCash')}</span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {filteredOrders.length === 0 && !statusFilter ? (
        <div className="text-center py-12 border rounded-lg">
          <Package className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
          <h3 className="text-lg font-medium mb-2">{t('orders.noOrdersYet')}</h3>
          <p className="text-muted-foreground mb-4">
            {t('orders.createFirstOrder')}
          </p>
          <Button asChild>
            <Link to={`/organizations/${orgId}/orders/new`}>
              <Plus className="w-4 h-4 mr-2" />
              {t('orders.newOrder')}
            </Link>
          </Button>
        </div>
      ) : filteredOrders.length === 0 ? (
        <div className="text-center py-12 border rounded-lg">
          <Package className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
          <h3 className="text-lg font-medium mb-2">{t('orders.noOrdersWithStatus')}</h3>
          <p className="text-muted-foreground">
            {t('orders.tryDifferentFilter')}
          </p>
        </div>
      ) : (
        <div className="border rounded-lg divide-y">
          {filteredOrders.map((order) => {
            const config = statusConfig[order.status] || statusConfig.pending;
            const StatusIcon = config.icon;
            return (
              <Link
                key={order.id}
                to={`/organizations/${orgId}/orders/${order.id}`}
                className="block p-4 hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-sm font-mono">
                        #{order.id.slice(-8).toUpperCase()}
                      </span>
                      <Badge variant={config.variant} className="flex items-center gap-1">
                        <StatusIcon className="w-3 h-3" />
                        {config.label}
                      </Badge>
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                      {order.customer_name && (
                        <div className="flex items-center gap-1">
                          <User className="w-3.5 h-3.5" />
                          <span>{order.customer_name}</span>
                        </div>
                      )}
                      {order.customer_phone && (
                        <div className="flex items-center gap-1">
                          <Phone className="w-3.5 h-3.5" />
                          <span>{order.customer_phone}</span>
                        </div>
                      )}
                      {order.gas_station_name && (
                        <div className="flex items-center gap-1">
                          <Store className="w-3.5 h-3.5" />
                          <span>{order.gas_station_name}</span>
                        </div>
                      )}
                      {order.driver_name ? (
                        <div className="flex items-center gap-1">
                          <Truck className="w-3.5 h-3.5" />
                          <span>{order.driver_name}</span>
                        </div>
                      ) : order.status === 'pending' && (
                        <div className="flex items-center gap-1 text-amber-600">
                          <Truck className="w-3.5 h-3.5" />
                          <span>{t('orders.noDriverAssigned')}</span>
                        </div>
                      )}
                    </div>
                    {order.customer_address && (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                        <MapPin className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">{order.customer_address}</span>
                      </div>
                    )}
                    <div className="text-xs text-muted-foreground mt-1">
                      {t('orders.itemCount', { count: order.item_count })} - {formatDate(order.created_at)}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-medium">{formatPrice(order.total_amount.toString(), organization.currency)}</div>
                    {order.is_paid ? (
                      <div className="flex items-center gap-1 text-xs text-green-600 justify-end mt-0.5">
                        <CreditCard className="w-3 h-3" />
                        <span>{t('orders.paid')}</span>
                      </div>
                    ) : order.payment_method === 'cash' && order.status === 'delivered' ? (
                      <div className="flex items-center gap-1 text-xs text-orange-600 justify-end mt-0.5">
                        <Banknote className="w-3 h-3" />
                        <span>{t('orders.awaitingCashCollection')}</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 text-xs text-amber-600 justify-end mt-0.5">
                        {order.payment_method === 'cash' ? <Banknote className="w-3 h-3" /> : <CreditCard className="w-3 h-3" />}
                        <span>{order.payment_method === 'cash' ? t('orders.paymentMethodCash') : t('orders.unpaid')}</span>
                      </div>
                    )}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
